import { useMutation, useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { memo, useEffect, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import Button from 'shopper/components/Button';

import useQueryClient from 'hooks/useQueryClient';

import { sendEvent } from 'lib/gtag';
import placeholder from 'lib/placeholder';
import { userQueryKeys } from 'lib/queryKeys';

import { useUser } from 'providers/UserProvider';

import {
  deleteUnfollowUser,
  getUserFollowingList,
  postFollowUser,
} from 'services/user';

const isFollowingUser = (followedUsers, userId) =>
  followedUsers?.length > 0 &&
  followedUsers.some(({ userId: followedUserId }) => followedUserId === userId);

const FollowUserButton = ({ className, gaEventCategory, userId, ...rest }) => {
  const {
    isLoggedIn,
    LoggedInUser,
    loadingLoggedInUser,
    runCallbackIfLoggedIn,
  } = useUser();
  const [buttonLabel, setButtonLabel] = useState(placeholder('actions.follow'));
  const queryClient = useQueryClient();

  const { data: followedUsers } = useQuery({
    queryKey: userQueryKeys.followersList({ userId: LoggedInUser?.userId }),
    queryFn: ({ signal }) => getUserFollowingList({ signal }),
    staleTime: Infinity,
    enabled: isLoggedIn,
    onSuccess: (followedUsersRes) => {
      if (isFollowingUser(followedUsersRes, userId)) {
        setButtonLabel(placeholder('actions.following'));
        return;
      }

      setButtonLabel(placeholder('actions.follow'));
    },
  });

  const { mutate: mutateFollow } = useMutation({
    mutationFn: postFollowUser,
    onMutate: () => setButtonLabel(placeholder('actions.following')),
    onSuccess: ({ followedUser }) => {
      queryClient.setQueryData(
        userQueryKeys.followersList({ userId: LoggedInUser?.userId }),
        [...followedUsers, followedUser]
      );
    },
  });

  const { mutate: mutateUnfollow } = useMutation({
    mutationFn: deleteUnfollowUser,
    onMutate: () => setButtonLabel(placeholder('actions.follow')),
    onSuccess: (_, { userId: user }) => {
      queryClient.setQueryData(
        userQueryKeys.followersList({ userId: LoggedInUser?.userId }),
        followedUsers.filter(
          ({ userId: followedUserId }) => followedUserId !== user
        )
      );
    },
  });

  const isFollowingUserMemoized = useMemo(
    () => isFollowingUser(followedUsers, userId),
    [followedUsers, userId]
  );

  const onClickHandler = () => {
    if (isFollowingUserMemoized) {
      sendEvent({ action: 'button_unfollow', category: gaEventCategory });
      runCallbackIfLoggedIn(mutateUnfollow, { userId });
      return;
    }

    sendEvent({
      action: 'button_follow',
      category: gaEventCategory,
    });
    runCallbackIfLoggedIn(mutateFollow, { userId });
  };

  const onMouseEnter = () => {
    if (isFollowingUserMemoized) {
      setButtonLabel(placeholder('actions.unfollow'));
    }
  };

  const onMouseLeave = () => {
    if (isFollowingUserMemoized) {
      setButtonLabel('Seguindo');
    }
  };

  useEffect(() => {
    setButtonLabel(
      isFollowingUserMemoized
        ? placeholder('actions.following')
        : placeholder('actions.follow')
    );
  }, [userId]);

  if (
    loadingLoggedInUser ||
    (!loadingLoggedInUser && !LoggedInUser?.canFollowUser(userId))
  ) {
    return null;
  }

  return (
    <Button
      className={twMerge('w-full md:w-unset', className)}
      size="size3"
      type={isFollowingUserMemoized ? 'primary' : 'primary-stroke'}
      onClick={onClickHandler}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...rest}
    >
      {buttonLabel}
    </Button>
  );
};

FollowUserButton.propTypes = {
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  gaEventCategory: PropTypes.string.isRequired,
  userId: PropTypes.number.isRequired,
};

export default memo(FollowUserButton);
